import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErr } from "@/lib/api";
import { track } from "@/lib/analytics";

const CATEGORIES = [
  { v: "bug", label: "Bug" },
  { v: "confusing", label: "Confusing" },
  { v: "feature_request", label: "Feature request" },
  { v: "other", label: "Other" },
];

/**
 * Lightweight feedback modal. Any org member can submit. Route is captured
 * automatically. Feedback is stored org-scoped; platform admins can see all.
 */
export default function FeedbackModal({ open, onOpenChange }) {
  const location = useLocation();
  const [category, setCategory] = useState("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setCategory("bug"); setTitle(""); setDescription(""); setBusy(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Please add a short title."); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        category,
        title: title.trim(),
        description: description.trim(),
        current_route: location.pathname,
      });
      track("feedback_submitted", { category });
      toast.success("Thanks — feedback recorded.");
      onOpenChange(false);
    } catch (e2) {
      toast.error(formatApiErr(e2.response?.data?.detail) || "Could not submit feedback.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby="feedback-desc" data-testid="feedback-modal">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription id="feedback-desc">
            Tell us what happened. We read every message.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="feedback-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Short title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} required data-testid="feedback-title" />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={4000}
              className="w-full min-h-[110px] rounded-md border border-zinc-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)]"
              placeholder="Steps to reproduce, expected result, or what you were hoping to do…"
              data-testid="feedback-description"
            />
          </div>
          <div className="text-[11px] text-zinc-500">
            Current page: <code>{location.pathname}</code>
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11 bg-[var(--brand-primary)] text-white" data-testid="feedback-submit">
            {busy ? "Sending…" : "Send feedback"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
